import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { supabase } from '@/lib/supabaseClient';
import { JOB_FIELDS } from '@/lib/jobsData';
import { CLIENT_FIELDS } from '@/lib/clientsData';

/**
 * DataContext — centralized cache for the two most-fetched tables in the app:
 * `jobs` and `clients`. Originally these were independently fetched on every
 * module mount (Dashboard, Active Jobs, Job Costing, Clients & Leads, …),
 * which caused redundant Supabase round-trips during normal navigation.
 *
 * Design goals:
 *   - LAZY: never fetch until the first consumer requests data.
 *   - SWR (stale-while-revalidate): consumers get the cached payload
 *     instantly. If the cache is older than {@link STALE_MS}, a background
 *     refresh runs and the consumer re-renders when it arrives.
 *   - FOCUS revalidation: when the tab regains visibility, mark caches stale
 *     so the next consumer triggers a refresh.
 *   - DEDUPED: while one request is in flight, concurrent consumers share it
 *     (no thundering-herd of duplicate selects).
 *   - MUTATION-AWARE: callers that mutate `jobs` or `clients` should invoke
 *     `refreshJobs()` / `refreshClients()` to refresh the cache.
 *
 * Consumers use {@link useData} at the top level of any page rendered under
 * `DashboardLayout` (where `<DataProvider>` is mounted).
 */
const DataContext = createContext(null);

/** Cache is considered fresh for this many milliseconds. */
const STALE_MS = 5 * 60 * 1000;

const initialResource = () => ({
  data: [],
  loading: false,
  error: null,
  loadedAt: 0,
  hasLoaded: false,
});

export const DataProvider = ({ children }) => {
  const [jobsState, setJobsState] = useState(initialResource);
  const [clientsState, setClientsState] = useState(initialResource);

  // In-flight promise dedupe (so concurrent consumers share one round-trip)
  const inflightRef = useRef({ jobs: null, clients: null });
  // Track active mount so we don't set state after unmount.
  // IMPORTANT: must re-set to true on every effect run, because in React
  // StrictMode (dev) the cleanup fires after the initial simulated unmount,
  // and useRef preserves the same instance across remounts. Without this
  // re-init, aliveRef.current stays `false` forever and every successful
  // fetch silently skips its setState, leaving the cache stuck in `loading`.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  /* ------------------------------------------------------------------------
   * Internal loaders
   * ---------------------------------------------------------------------- */
  const loadJobsInternal = useCallback(async () => {
    if (inflightRef.current.jobs) return inflightRef.current.jobs;
    const p = (async () => {
      setJobsState((cur) => ({ ...cur, loading: true, error: null }));
      try {
        const { data, error } = await supabase
          .from('jobs')
          .select(JOB_FIELDS)
          .order('created_at', { ascending: false });
        if (error) throw error;
        if (aliveRef.current) {
          setJobsState({
            data: data || [],
            loading: false,
            error: null,
            loadedAt: Date.now(),
            hasLoaded: true,
          });
        }
        return data || [];
      } catch (err) {
        if (aliveRef.current) {
          setJobsState((cur) => ({
            ...cur,
            loading: false,
            error: err?.message || 'Failed to load jobs.',
          }));
        }
        throw err;
      } finally {
        inflightRef.current.jobs = null;
      }
    })();
    inflightRef.current.jobs = p;
    return p;
  }, []);

  const loadClientsInternal = useCallback(async () => {
    if (inflightRef.current.clients) return inflightRef.current.clients;
    const p = (async () => {
      setClientsState((cur) => ({ ...cur, loading: true, error: null }));
      try {
        const { data, error } = await supabase
          .from('clients')
          .select(CLIENT_FIELDS)
          .order('created_at', { ascending: false });
        if (error) throw error;
        if (aliveRef.current) {
          setClientsState({
            data: data || [],
            loading: false,
            error: null,
            loadedAt: Date.now(),
            hasLoaded: true,
          });
        }
        return data || [];
      } catch (err) {
        if (aliveRef.current) {
          setClientsState((cur) => ({
            ...cur,
            loading: false,
            error: err?.message || 'Failed to load clients.',
          }));
        }
        throw err;
      } finally {
        inflightRef.current.clients = null;
      }
    })();
    inflightRef.current.clients = p;
    return p;
  }, []);

  /* ------------------------------------------------------------------------
   * Public actions
   * ---------------------------------------------------------------------- */
  /** Lazy-load jobs the first time, then revalidate in background if stale. */
  const ensureJobs = useCallback(() => {
    if (!jobsState.hasLoaded && !jobsState.loading) return loadJobsInternal();
    if (jobsState.hasLoaded && Date.now() - jobsState.loadedAt > STALE_MS) {
      // Stale → background refresh (don't block the consumer)
      loadJobsInternal().catch(() => {});
    }
    return Promise.resolve(jobsState.data);
  }, [jobsState.hasLoaded, jobsState.loading, jobsState.loadedAt, jobsState.data, loadJobsInternal]);

  const ensureClients = useCallback(() => {
    if (!clientsState.hasLoaded && !clientsState.loading) return loadClientsInternal();
    if (clientsState.hasLoaded && Date.now() - clientsState.loadedAt > STALE_MS) {
      loadClientsInternal().catch(() => {});
    }
    return Promise.resolve(clientsState.data);
  }, [
    clientsState.hasLoaded,
    clientsState.loading,
    clientsState.loadedAt,
    clientsState.data,
    loadClientsInternal,
  ]);

  /** Force a refresh — used after mutations. */
  const refreshJobs = useCallback(() => loadJobsInternal(), [loadJobsInternal]);
  const refreshClients = useCallback(() => loadClientsInternal(), [loadClientsInternal]);
  const refreshAll = useCallback(
    () => Promise.all([loadJobsInternal(), loadClientsInternal()]),
    [loadJobsInternal, loadClientsInternal]
  );

  /** Mark stale without immediately refetching (next consumer will trigger). */
  const invalidateJobs = useCallback(() => {
    setJobsState((cur) => ({ ...cur, loadedAt: 0 }));
  }, []);
  const invalidateClients = useCallback(() => {
    setClientsState((cur) => ({ ...cur, loadedAt: 0 }));
  }, []);

  /**
   * Optimistic local upsert/remove helpers so the UI stays in sync without
   * paying for a full refetch round-trip after small mutations.
   */
  const upsertJobInCache = useCallback((updated) => {
    if (!updated?.id) return;
    setJobsState((cur) => {
      const idx = cur.data.findIndex((j) => j.id === updated.id);
      const next =
        idx >= 0
          ? cur.data.map((j) => (j.id === updated.id ? { ...j, ...updated } : j))
          : [updated, ...cur.data];
      return { ...cur, data: next };
    });
  }, []);

  const removeJobFromCache = useCallback((id) => {
    if (!id) return;
    setJobsState((cur) => ({ ...cur, data: cur.data.filter((j) => j.id !== id) }));
  }, []);

  const upsertClientInCache = useCallback((updated) => {
    if (!updated?.id) return;
    setClientsState((cur) => {
      const idx = cur.data.findIndex((c) => c.id === updated.id);
      const next =
        idx >= 0
          ? cur.data.map((c) => (c.id === updated.id ? { ...c, ...updated } : c))
          : [updated, ...cur.data];
      return { ...cur, data: next };
    });
  }, []);

  const removeClientFromCache = useCallback((id) => {
    if (!id) return;
    setClientsState((cur) => ({ ...cur, data: cur.data.filter((c) => c.id !== id) }));
  }, []);

  /* ------------------------------------------------------------------------
   * Focus revalidation — when the tab transitions from `hidden` to `visible`
   * AND the cache hasn't been touched in {@link STALE_MS}, trigger a
   * background refresh. We intentionally do NOT invalidate on every
   * visibility tick (browsers fire `visible` during regular navigation),
   * which would defeat the dedupe goal.
   * ---------------------------------------------------------------------- */
  const wasHiddenRef = useRef(false);
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        wasHiddenRef.current = true;
        return;
      }
      if (document.visibilityState !== 'visible') return;
      if (!wasHiddenRef.current) return; // Spurious "visible" — ignore.
      wasHiddenRef.current = false;
      // Only invalidate caches that are actually stale.
      const now = Date.now();
      if (jobsState.hasLoaded && now - jobsState.loadedAt > STALE_MS) {
        invalidateJobs();
      }
      if (clientsState.hasLoaded && now - clientsState.loadedAt > STALE_MS) {
        invalidateClients();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [
    jobsState.hasLoaded,
    jobsState.loadedAt,
    clientsState.hasLoaded,
    clientsState.loadedAt,
    invalidateJobs,
    invalidateClients,
  ]);

  /* ------------------------------------------------------------------------
   * Memoized context value
   * ---------------------------------------------------------------------- */
  const value = useMemo(
    () => ({
      jobs: jobsState.data,
      jobsLoading: jobsState.loading,
      jobsError: jobsState.error,
      jobsLoadedAt: jobsState.loadedAt,
      hasLoadedJobs: jobsState.hasLoaded,

      clients: clientsState.data,
      clientsLoading: clientsState.loading,
      clientsError: clientsState.error,
      clientsLoadedAt: clientsState.loadedAt,
      hasLoadedClients: clientsState.hasLoaded,

      ensureJobs,
      ensureClients,
      refreshJobs,
      refreshClients,
      refreshAll,
      invalidateJobs,
      invalidateClients,

      upsertJobInCache,
      removeJobFromCache,
      upsertClientInCache,
      removeClientFromCache,
    }),
    [
      jobsState,
      clientsState,
      ensureJobs,
      ensureClients,
      refreshJobs,
      refreshClients,
      refreshAll,
      invalidateJobs,
      invalidateClients,
      upsertJobInCache,
      removeJobFromCache,
      upsertClientInCache,
      removeClientFromCache,
    ]
  );

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

/**
 * Primary hook — read the entire cache + all actions.
 */
export const useData = () => {
  const ctx = useContext(DataContext);
  if (!ctx) {
    throw new Error('useData must be used inside <DataProvider>');
  }
  return ctx;
};

/**
 * Convenience: lazy-load jobs on mount and read the cached list.
 *
 * Returns: { jobs, loading, error, refresh, loadedAt, hasLoaded }
 */
export const useJobs = () => {
  const {
    jobs,
    jobsLoading,
    jobsError,
    jobsLoadedAt,
    hasLoadedJobs,
    ensureJobs,
    refreshJobs,
  } = useData();
  useEffect(() => {
    ensureJobs();
  }, [ensureJobs]);
  return {
    jobs,
    loading: jobsLoading,
    error: jobsError,
    loadedAt: jobsLoadedAt,
    hasLoaded: hasLoadedJobs,
    refresh: refreshJobs,
  };
};

/**
 * Convenience: lazy-load clients on mount and read the cached list.
 *
 * Returns: { clients, loading, error, refresh, loadedAt, hasLoaded }
 */
export const useClients = () => {
  const {
    clients,
    clientsLoading,
    clientsError,
    clientsLoadedAt,
    hasLoadedClients,
    ensureClients,
    refreshClients,
  } = useData();
  useEffect(() => {
    ensureClients();
  }, [ensureClients]);
  return {
    clients,
    loading: clientsLoading,
    error: clientsError,
    loadedAt: clientsLoadedAt,
    hasLoaded: hasLoadedClients,
    refresh: refreshClients,
  };
};

export default DataContext;
